// apps/spa/src/components/reminders/ReminderEditorForm.tsx
// REMIND-03 + REMIND-04 — full-page editor mounted by /reminders/new and
// /reminders/:id/edit. Mounts inside ONE FormProvider so EmbedEditor +
// DiscordEmbedPreview + ChannelPicker + ScheduleEditor all share the same
// react-hook-form state.
//
// LOCKED PATTERN (RESEARCH Pattern 7 — boundary translator):
//   The resolver binds `reminderFormSchema`, NOT `createReminderSchema`.
//   `reminderFormSchema` is a parallel schema with `embed` as an OBJECT
//   piped through the SAME `embedSchema` from packages/shared (single source
//   of validation truth). This is what lets:
//     - embedSchema.superRefine fire per-keystroke (the 6,000-char check)
//     - server-issued issue paths like ['embed', 'title'] map directly to
//       RHF setError without translation
//   The transform-out to wire shape (embedJson stringification, color
//   hex->int, scheduleCron synthesis) happens ONLY in onSubmit.
//
// Layout:
//   * lg+ : 55% / 45% two-column grid (form left, sticky preview right)
//   * <lg : Tabs (Edit / Preview) — preview shown inside the Preview tab
//
// Note on the mobile/desktop split: we render EmbedEditor + DiscordEmbedPreview
// EXACTLY ONCE under the FormProvider (the desktop column shows preview
// always; mobile users get the same instance via the Tabs preview tab —
// no duplicate FormField mounts to avoid id collisions).

import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { BASTN_GOLD, type Embed } from '@guardian/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  DiscordEmbedPreview,
  EmbedEditor,
  totalEmbedChars,
} from '@/components/EmbedEditor';
import { hexToInt, intToHex, isValidHex } from '@/lib/color';
import {
  nextRunRelative,
  presetToCron,
  eventTriggerFromPreset,
  type SchedulePreset,
} from '@/lib/schedule';
import {
  useCreateReminder,
  useUpdateReminder,
  type ReminderWriteBody,
} from '@/hooks/useReminders';
import { ApiError } from '@/lib/api';
import { ChannelPicker } from './ChannelPicker';
import { ScheduleEditor } from './ScheduleEditor';
import { TotalCharCounter } from './TotalCharCounter';
import { DmTestButton } from './DmTestButton';

// ---------------------------------------------------------------------------
// Form-shaped schema — Pattern 7 boundary translator. embed is an OBJECT here.
// ---------------------------------------------------------------------------

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const schedulePresetSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('manual') }),
  z.object({
    type: z.literal('daily'),
    hhmm: z.string().regex(HHMM_RE),
  }),
  z.object({
    type: z.literal('weekly'),
    dayOfWeek: z.union([
      z.literal(0),
      z.literal(1),
      z.literal(2),
      z.literal(3),
      z.literal(4),
      z.literal(5),
      z.literal(6),
    ]),
    hhmm: z.string().regex(HHMM_RE),
  }),
  // Phase 4 (Plan 04-02): event-trigger preset
  z.object({
    type: z.literal('event'),
    eventId: z.string().optional(),
    eventMatchMode: z.enum(['specific', 'all']),
    leadMinutes: z.number().int().positive(),
  }),
]);

// Form-side embed schema — same per-field limits as packages/shared embedSchema,
// but `color` is a hex STRING (form state) instead of int (wire shape). The
// 6,000-char `_total` rule is replicated here so it fires on every keystroke;
// the wire-side embedSchema's superRefine still runs server-side as the
// authoritative gate (defense in depth, RESEARCH Pattern 4).
const URL_OR_EMPTY = z.union([z.string().url(), z.literal('')]);

const embedFieldFormSchema = z.object({
  name: z.string().min(1).max(256),
  value: z.string().min(1).max(1024),
  inline: z.boolean().optional(),
});

const embedFormSchema = z
  .object({
    title: z.string().max(256).optional(),
    description: z.string().max(4096).optional(),
    url: URL_OR_EMPTY.optional(),
    color: z
      .string()
      .optional()
      .refine((v) => v === undefined || v === '' || isValidHex(v), {
        message: 'Color must be a 6-digit hex value (e.g. #d4af37).',
      }),
    footer: z
      .object({
        text: z.string().max(2048),
        icon_url: URL_OR_EMPTY.optional(),
      })
      .optional(),
    thumbnail: z.object({ url: URL_OR_EMPTY }).optional(),
    image: z.object({ url: URL_OR_EMPTY }).optional(),
    fields: z.array(embedFieldFormSchema).max(25).optional(),
  })
  .superRefine((embed, ctx) => {
    // Mirror packages/shared/embed.ts superRefine for the 6,000-char total.
    let total = 0;
    total += embed.title?.length ?? 0;
    total += embed.description?.length ?? 0;
    total += embed.footer?.text.length ?? 0;
    for (const f of embed.fields ?? []) {
      total += f.name.length + f.value.length;
    }
    if (total > 6000) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Total embed text is ${total} characters; max 6000.`,
        path: ['_total'],
      });
    }
  });

const reminderFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  channelId: z.string().min(1, 'Channel is required'),
  schedulePreset: schedulePresetSchema,
  embed: embedFormSchema,
});

export type ReminderFormValues = z.infer<typeof reminderFormSchema>;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ReminderEditorFormProps {
  mode: 'new' | 'edit';
  reminderId?: string;
  defaultValues?: ReminderFormValues;
  // Phase 5 (Plan 05-02): when editing a reminder whose disabledReason is
  // 'auto_failure', the form renders a recovery notice and forces enabled=true
  // in the submit body (defense-in-depth; server also enforces this).
  wasAutoDisabled?: boolean;
}

const FALLBACK_DEFAULTS: ReminderFormValues = {
  name: '',
  channelId: '',
  schedulePreset: { type: 'daily', hhmm: '09:00' },
  embed: {
    title: '',
    description: '',
    color: intToHex(BASTN_GOLD),
    footer: { text: '', icon_url: '' },
    thumbnail: { url: '' },
    image: { url: '' },
    url: '',
  },
};

export function ReminderEditorForm({
  mode,
  reminderId,
  defaultValues,
  wasAutoDisabled = false,
}: ReminderEditorFormProps) {
  const navigate = useNavigate();
  const createMutation = useCreateReminder();
  const updateMutation = useUpdateReminder(reminderId ?? '');

  const form = useForm<ReminderFormValues>({
    resolver: zodResolver(reminderFormSchema),
    defaultValues: defaultValues ?? FALLBACK_DEFAULTS,
  });

  // Reset form when defaultValues become available asynchronously (edit mode).
  useEffect(() => {
    if (defaultValues) form.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  const embedValues = useWatch({ control: form.control, name: 'embed' });
  const total = totalEmbedChars(embedValues as Partial<Embed>);

  async function onSubmit(values: ReminderFormValues) {
    try {
      // ------------------- Boundary translator (form -> wire) -------------------
      const colorHex = values.embed.color;
      const color =
        colorHex && isValidHex(colorHex) ? hexToInt(colorHex) : undefined;

      // Strip empty optional fields — Discord rejects `url: ''` in some places,
      // and the wire embedSchema treats empty strings as URL_OR_EMPTY but a
      // tighter outbound shape keeps stored embeds clean.
      const embedObj: Record<string, unknown> = { ...values.embed, color };
      if (color === undefined) delete embedObj.color;
      if (!embedObj.title) delete embedObj.title;
      if (!embedObj.description) delete embedObj.description;
      if (!embedObj.url) delete embedObj.url;
      const footer = embedObj.footer as { text?: string; icon_url?: string } | undefined;
      if (!footer?.text) {
        delete embedObj.footer;
      } else if (!footer.icon_url) {
        delete (footer as { icon_url?: string }).icon_url;
      }
      const thumbnail = embedObj.thumbnail as { url?: string } | undefined;
      if (!thumbnail?.url) delete embedObj.thumbnail;
      const image = embedObj.image as { url?: string } | undefined;
      if (!image?.url) delete embedObj.image;

      const preset = values.schedulePreset as SchedulePreset;
      const scheduleCron = presetToCron(preset);
      const eventTrigger = eventTriggerFromPreset(preset);

      const body: ReminderWriteBody = {
        name: values.name,
        channelId: values.channelId,
        embedJson: JSON.stringify(embedObj),
        scheduleCron: scheduleCron ?? undefined,
        triggerType: preset.type === 'event' ? 'event' : 'cron',
        // Edit mode preserves the existing enabled flag from defaultValues
        // metadata (passed via the parent route's loader). New mode defaults true.
        // Phase 5 (Plan 05-02): when previously auto-disabled, force enabled=true
        // on submit so Save acts as the recovery action (server-side enforces
        // this too — defense-in-depth).
        enabled: true,
        // Phase 4 (Plan 04-02): event-trigger binding fields
        event_id:           eventTrigger?.event_id ?? null,
        event_match_mode:   eventTrigger?.event_match_mode ?? null,
        event_lead_minutes: eventTrigger?.event_lead_minutes ?? null,
      };

      const mutation = mode === 'new' ? createMutation : updateMutation;
      await mutation.mutateAsync(body);

      const next = scheduleCron ? nextRunRelative(scheduleCron) : 'Manual';
      if (mode === 'new') {
        toast.success(
          scheduleCron
            ? `✓ Saved — next post ${next}`
            : '✓ Manual reminder saved',
        );
      } else {
        toast.success(
          scheduleCron
            ? `✓ Updated — next post ${next}`
            : '✓ Updated — manual reminder',
        );
      }
      navigate('/reminders');
    } catch (err) {
      // Map server validation issues to RHF (ApiError.details.issues —
      // the worker emits zod's ZodIssue[] in this slot from zValidator).
      if (err instanceof ApiError) {
        const details = err.details as
          | { issues?: Array<{ path: Array<string | number>; message: string }> }
          | undefined;
        const issues = details?.issues;
        if (Array.isArray(issues)) {
          for (const issue of issues) {
            const path = Array.isArray(issue.path)
              ? issue.path.join('.')
              : String(issue.path);
            // Best-effort path mapping — RHF accepts dot-paths for nested fields.
            // Server-issued paths land directly on the form-shaped tree because
            // reminderFormSchema mirrors the server's shape (with embed as object).
            // Cast is safe — we're forwarding zod's path back into a parallel schema.
            form.setError(path as Parameters<typeof form.setError>[0], {
              type: 'server',
              message: issue.message,
            });
          }
          return;
        }
      }
      // 401 already dispatched by the api wrapper; SessionExpiredModal handles it.
      // Anything else: surface a generic toast so the operator isn't stuck.
      toast.error('Could not save reminder. Try again.');
    }
  }

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6"
        noValidate
      >
        <header className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary/80">Reminders</p>
            <h1 className="font-display text-3xl">
              {mode === 'new' ? 'New reminder' : 'Edit reminder'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <DmTestButton reminderId={reminderId} />
            <Button type="submit" disabled={form.formState.isSubmitting}>
              Save
            </Button>
          </div>
        </header>

        {wasAutoDisabled && (
          <div
            role="alert"
            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm"
            data-testid="auto-disabled-recovery-notice"
          >
            This reminder was auto-disabled after repeated failures. Save will
            fix the issue and re-enable it.
          </div>
        )}

        <TotalCharCounter total={total} max={6000} />

        {/* Single form mount + responsive preview placement.
            FormSection renders ONCE (avoids RHF FormItem useId() collisions
            from duplicate mounts under one FormProvider). The preview lives
            inside a Tabs container on mobile (<lg) and as a sticky right
            column on desktop (lg+) — same DiscordEmbedPreview instance read
            from useWatch. The desktop preview is hidden below lg via Tailwind
            display utilities; the Tabs trigger row is hidden above lg. */}
        <div className="lg:grid lg:grid-cols-[55%_45%] lg:gap-8">
          <div className="glass p-6 space-y-6">
            <Tabs defaultValue="edit" className="lg:hidden">
              <TabsList>
                <TabsTrigger value="edit">Edit</TabsTrigger>
                <TabsTrigger value="preview">Preview · Live</TabsTrigger>
              </TabsList>
              <TabsContent value="edit" />
              <TabsContent value="preview">
                <DiscordEmbedPreview embed={embedValues as Embed} />
              </TabsContent>
            </Tabs>
            <FormSection form={form} />
          </div>
          <div className="hidden lg:block">
            <div className="sticky top-20">
              <DiscordEmbedPreview embed={embedValues as Embed} />
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

// Extracted so the same FormProvider-bound fields render in both the mobile
// Tabs Edit panel and the desktop left column. RHF tracks fields by name —
// rendering the SAME named fields in both panels would be a duplicate-mount
// id collision via FormItem's React.useId(). Solved by the lg:hidden /
// hidden lg:grid split above: only one panel mounts at a time per breakpoint
// because Tailwind's display utility removes the other branch from the DOM.
function FormSection({
  form,
}: {
  form: ReturnType<typeof useForm<ReminderFormValues>>;
}) {
  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input maxLength={100} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="channelId"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Channel</FormLabel>
            <FormControl>
              <ChannelPicker
                value={field.value}
                onChange={field.onChange}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div>
        <p className="text-xs uppercase tracking-widest text-primary/80 mb-3">Schedule</p>
        <FormField
          control={form.control}
          name="schedulePreset"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Schedule</FormLabel>
              <FormControl>
                <ScheduleEditor
                  value={field.value as SchedulePreset}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div>
        <p className="text-xs uppercase tracking-widest text-primary/80 mb-3">Embed</p>
        <EmbedEditor name="embed" />
      </div>
    </div>
  );
}
